import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Button,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  EditOutlined,
  ExportOutlined,
  PictureOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
// Api
import { GET_TICKET_EVENT } from 'api/ticketing/queries';
// Types
import {
  GetTicketEvent,
  GetTicketEventVariables,
} from 'api/ticketing/types/GetTicketEvent';
import { TicketEventStatus, TicketOrderKind } from 'api/graphql-global-types';
// Helpers
import {
  EventLifecycleAction,
  formatEventDateTime,
  getEventHostName,
  getEventStatusMeta,
  isTaxApprovalPending,
} from 'pages/Events/helpers';
// Constants
import {
  EDIT_TICKET_EVENT_GEN,
  TICKET_EVENT_DETAILS_GEN,
} from 'constants/routes';
import { EventDetailsTab } from 'pages/Events/constants';
import { getEventPublicUrl } from 'pages/Events/links';
// Components
import LoadErrorAlert from 'pages/Events/components/LoadErrorAlert/LoadErrorAlert';
import StatusTag from 'pages/Events/components/StatusTag/StatusTag';
import TicketingStatusAlert from 'pages/Events/components/TicketingStatusAlert/TicketingStatusAlert';
import LifecycleActions from './components/LifecycleActions/LifecycleActions';
import OverviewTab from './components/OverviewTab/OverviewTab';
import OrdersTable from './components/OrdersTable/OrdersTable';
import CompsTab from './components/CompsTab/CompsTab';
import TiersTab from './components/TiersTab/TiersTab';
import PresaleCodesTab from './components/PresaleCodesTab/PresaleCodesTab';
import DoorLogTab from './components/DoorLogTab/DoorLogTab';
import PeopleTab from './components/PeopleTab/PeopleTab';
import MatchcardTab from './components/MatchcardTab/MatchcardTab';
import ShareModal from './components/ShareModal/ShareModal';
// Styles
import styles from './TicketEventDetails.module.scss';

const { TabPane } = Tabs;
const { Title, Text } = Typography;

const TAB_KEYS = Object.values(EventDetailsTab) as string[];

const TicketEventDetails = (): JSX.Element => {
  const history = useHistory();
  const [shareOpen, setShareOpen] = useState(false);
  const { ticketEventId, eventDetailsTab } = useParams<{
    ticketEventId: string;
    eventDetailsTab?: string;
  }>();
  const activeTab = TAB_KEYS.includes(eventDetailsTab || '')
    ? (eventDetailsTab as EventDetailsTab)
    : EventDetailsTab.Overview;

  const { data, loading, error, refetch } = useQuery<
    GetTicketEvent,
    GetTicketEventVariables
  >(GET_TICKET_EVENT, {
    variables: {
      input: { ticketEventId },
    },
    fetchPolicy: 'cache-and-network',
  });

  const event = data?.hostedTicketEvent;
  const tiers = data?.hostedTicketTiers || [];

  const goToTab = (tab: string): void =>
    history.push(`${TICKET_EVENT_DETAILS_GEN}/${ticketEventId}/${tab}`);

  const handleLifecycleChange = (action?: EventLifecycleAction): void => {
    refetch();

    // Cancelling never refunds by itself; take the admin to the paid orders
    if (action === 'cancel') {
      goToTab(EventDetailsTab.Orders);
    }
  };

  if (error && !event) {
    return (
      <LoadErrorAlert
        message="Failed to load the event"
        error={error}
        onRetry={refetch}
      />
    );
  }

  if (!event) {
    return (
      <div className={styles.loading}>
        {loading ? <Spin /> : <Text>Event not found.</Text>}
      </div>
    );
  }

  const publicUrl = getEventPublicUrl(event);

  return (
    <div className={styles.root}>
      <TicketingStatusAlert className={styles.alert} />

      <header className={styles.header}>
        <Avatar
          shape="square"
          size={88}
          src={event.coverImageUrl || undefined}
          icon={<PictureOutlined />}
          className={styles.cover}
          alt=""
        />
        <div className={styles.headline}>
          <Space size={6} wrap>
            <StatusTag
              meta={getEventStatusMeta(event.status, event.ticketsEnabled)}
            />
            {isTaxApprovalPending(event) && (
              <Tag color="orange">Tax not approved</Tag>
            )}
            {event.rescheduledAt && <Tag>Rescheduled</Tag>}
          </Space>
          <Title level={3} className={styles.title}>
            {event.name}
          </Title>
          <Text type="secondary">
            {formatEventDateTime(event.startsAt, event.timezone)}
            {event.venueName ? ` · ${event.venueName}` : ''}
            {event.venueAddress?.city ? `, ${event.venueAddress.city}` : ''}
          </Text>
          <Text type="secondary">
            Hosted by <Text strong>{getEventHostName(event)}</Text>
          </Text>
        </div>
        <Space className={styles.actions} wrap>
          {publicUrl && (
            <Button
              icon={<ExportOutlined />}
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View on web
            </Button>
          )}
          {event.status !== TicketEventStatus.Cancelled && (
            <Button
              icon={<ShareAltOutlined />}
              onClick={() => setShareOpen(true)}
            >
              Share
            </Button>
          )}
          {event.status !== TicketEventStatus.Cancelled && (
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                history.push(`${EDIT_TICKET_EVENT_GEN}/${event.id}`)
              }
            >
              Edit
            </Button>
          )}
          <LifecycleActions event={event} onChanged={handleLifecycleChange} />
        </Space>
      </header>

      {event.status === TicketEventStatus.Cancelled && (
        <Alert
          className={styles.alert}
          type="warning"
          showIcon
          message="This event is cancelled."
          description="Paid orders aren't refunded automatically. Use Refund all paid orders on the Orders & refunds tab, or refund them one by one."
        />
      )}

      <Tabs activeKey={activeTab} onChange={goToTab} destroyInactiveTabPane>
        <TabPane tab="Overview" key={EventDetailsTab.Overview}>
          <OverviewTab event={event} />
        </TabPane>
        <TabPane tab="Orders & refunds" key={EventDetailsTab.Orders}>
          <OrdersTable event={event} kind={TicketOrderKind.Purchase} />
        </TabPane>
        <TabPane tab="Comps" key={EventDetailsTab.Comps}>
          <CompsTab event={event} tiers={tiers} />
        </TabPane>
        <TabPane tab={`Tiers (${tiers.length})`} key={EventDetailsTab.Tiers}>
          <TiersTab event={event} tiers={tiers} onChanged={() => refetch()} />
        </TabPane>
        <TabPane tab="Participants & affiliates" key={EventDetailsTab.People}>
          <PeopleTab event={event} />
        </TabPane>
        <TabPane tab="Matchcard" key={EventDetailsTab.Matchcard}>
          <MatchcardTab event={event} onChanged={() => refetch()} />
        </TabPane>
        <TabPane tab="Presale codes" key={EventDetailsTab.PresaleCodes}>
          <PresaleCodesTab event={event} tiers={tiers} />
        </TabPane>
        <TabPane tab="Door log" key={EventDetailsTab.DoorLog}>
          <DoorLogTab event={event} />
        </TabPane>
      </Tabs>

      <ShareModal
        event={event}
        visible={shareOpen}
        onClose={() => setShareOpen(false)}
      />
    </div>
  );
};

export default TicketEventDetails;
